/**
 * The landing page's picture tiles: their look, their scroll reveal and the
 * gentle idle float of their icons.
 *
 * ── WHY A SHEET OF ITS OWN ──────────────────────────────────────────────────
 * The tiles are built from THE ONE RECIPE in `app.css` (`--rga-face`,
 * `--rga-lift-1`, `--rga-sheen`, `--rga-radius-panel` and the colour tokens), so
 * the landing reads as the same game as the screens behind the sign-in. Only
 * the motion and the tile layout live here, injected once per document the same
 * way `cta.ts` injects the disclosure chevron — this module owns no colour.
 *
 * ── REDUCED MOTION ──────────────────────────────────────────────────────────
 * Twice over. The sheet switches both animations off under
 * `prefers-reduced-motion`, and `armReveal` never hides anything in the first
 * place when the preference is set — so a reader who asked for no motion never
 * sees a tile fade in, and nothing waits on an observer to become visible.
 */
import { systemPrefersReducedMotion } from '../a11y/motion';

export const LANDING_STYLE_ID = 'rga-landing-tiles-css';

export const LANDING_CSS = `
.rga-lp-tile{position:relative;display:flex;flex-direction:column;align-items:center;text-align:center;gap:.5rem;
  padding:1.1rem .9rem 1rem;background:var(--rga-face),var(--color-surface);
  border:var(--rga-rim,2px) solid var(--color-border);border-radius:var(--rga-radius-panel,14px);
  box-shadow:var(--rga-lift-1),var(--rga-sheen)}
.rga-lp-art{display:flex;align-items:center;justify-content:center;
  filter:drop-shadow(0 8px 10px rgba(4,9,24,.45))}
.rga-lp-art img{display:block;object-fit:contain}
.rga-lp-figure{font-family:var(--font-heading);color:var(--color-gold);font-size:1.9rem;line-height:1}
.rga-lp-label{color:var(--color-text-dim);font-size:.85rem;line-height:1.25}
.rga-lp-badge{position:absolute;top:-.65rem;left:-.65rem;display:flex;align-items:center;justify-content:center;
  width:2.2rem;height:2.2rem;border-radius:999px;background:var(--color-gold);color:var(--color-bg);
  font-family:var(--font-heading);font-size:1.05rem;box-shadow:var(--rga-lift-1)}
.rga-lp-steps>li{position:relative}
@media (min-width:640px){
  .rga-lp-steps>li:not(:last-child)::after{content:"";position:absolute;top:50%;right:-1.05rem;width:.9rem;height:.9rem;
    border-top:4px solid var(--color-gold);border-right:4px solid var(--color-gold);border-radius:2px;
    transform:translateY(-50%) rotate(45deg);opacity:.85}
}
.rga-lp-float{animation:rga-lp-float 4.6s ease-in-out infinite}
@keyframes rga-lp-float{0%,100%{translate:0 0}50%{translate:0 -6px}}
.rga-lp-reveal{transition:opacity .55s ease-out,translate .55s cubic-bezier(.2,.8,.2,1)}
.rga-lp-reveal[data-reveal="wait"]{opacity:0;translate:0 18px}
@media (prefers-reduced-motion:reduce){
  .rga-lp-float{animation:none}
  .rga-lp-reveal{transition:none}
  .rga-lp-reveal[data-reveal="wait"]{opacity:1;translate:0 0}
}
`;

export function ensureLandingStyles(doc: Document): void {
  if (doc.getElementById(LANDING_STYLE_ID) !== null) return;
  const style = doc.createElement('style');
  style.id = LANDING_STYLE_ID;
  style.textContent = LANDING_CSS;
  doc.head.append(style);
}

/**
 * Fade every `.rga-lp-reveal` under `root` in as it scrolls into view. Returns
 * the teardown. Without `IntersectionObserver` (jsdom, an old browser) or under
 * reduced motion, nothing is hidden at all — content never depends on the
 * observer firing.
 */
export function armReveal(root: HTMLElement): () => void {
  const win = root.ownerDocument.defaultView;
  const IO = win?.IntersectionObserver;
  if (IO === undefined || systemPrefersReducedMotion()) return () => undefined;
  const items = [...root.querySelectorAll<HTMLElement>('.rga-lp-reveal')];
  const observer = new IO(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        (entry.target as HTMLElement).removeAttribute('data-reveal');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
  );
  for (const item of items) {
    item.setAttribute('data-reveal', 'wait');
    observer.observe(item);
  }
  return () => {
    observer.disconnect();
    for (const item of items) item.removeAttribute('data-reveal');
  };
}
